import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, MessageSquarePlus, Pencil } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/toaster';
import { usePatient, usePatientClinics, useUpdatePatient } from '@/hooks/queries';
import { useAuthStore } from '@/auth/store';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { clearActivePatient } from '@/hooks/useActivePatient';
import { EditPatientDialog } from './EditPatientDialog';
import { SendTipDialog } from './SendTipDialog';
import { PatientNotesButton, PatientNotesSheet } from './PatientNotesSheet';

/**
 * Patient identity for the global fixed header (portaled into its left slot by
 * PatientShell): patient name (prominent) + id (muted) and the profile actions.
 * Stays visible while everything below scrolls.
 *
 * The "All patients" back link lives here (to the left of the name) so it stays
 * visible even when the rail is collapsed; the rail keeps only its collapse
 * toggle. "Send a link" was dropped; only Edit profile + Send a tip remain.
 */
type Props = {
  patientId: number;
};

export const PatientHeaderBar = ({ patientId }: Props) => {
  const { t } = useTranslation();
  const patientQuery = usePatient(patientId);
  const patientClinicsQuery = usePatientClinics(patientId);
  const clinicianFlags = useAuthStore(
    (s) => s.clinician?.flags as Record<string, unknown> | undefined,
  );
  const updatePatient = useUpdatePatient(patientId);
  const patient = patientQuery.data;

  const [editOpen, setEditOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);

  const fullName =
    `${patient?.first_name ?? ''} ${patient?.last_name ?? ''}`.trim() ||
    t('Patient #{{id}}', { id: patientId });

  // Age from birthday; shown next to the id along with sex.
  const age = useMemo(() => {
    const bday = patient?.birthday;
    if (!bday) return null;
    const d = new Date(bday);
    if (Number.isNaN(d.getTime())) return null;
    return new Date().getFullYear() - d.getFullYear();
  }, [patient?.birthday]);

  const metaLabel = [
    patient?.sex
      ? t(patient.sex, { defaultValue: patient.sex.replace(/_/g, ' ') })
      : null,
    age !== null ? `${age} ${t('yrs')}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const onSaveProfile = async (body: Parameters<typeof updatePatient.mutateAsync>[0]) => {
    try {
      await updatePatient.mutateAsync(body);
      toast({ title: t('Patient updated') });
      track(ANALYTICS_EVENTS.PATIENT_EDITED);
      setEditOpen(false);
    } catch {
      toast({ title: t('Could not update patient'), variant: 'destructive' });
    }
  };

  return (
    <div className="flex min-w-0 items-center gap-2">
      {/* Back to the patient list — lives in the header (not the rail) so it
          stays visible even when the rail is collapsed. */}
      <Link
        to="/patients"
        onClick={() => clearActivePatient()}
        title={t('All patients')}
        className="inline-flex shrink-0 items-center gap-1 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        <ChevronLeft className="h-4 w-4" />
        {t('All patients')}
      </Link>
      <span aria-hidden className="mx-2 h-5 w-px shrink-0 bg-border" />

      {/* Name (prominent) + id (muted). */}
      <div className="flex min-w-0 items-baseline gap-2">
        {patientQuery.isLoading ? (
          <Skeleton className="h-5 w-32" />
        ) : (
          <span className="truncate text-base font-semibold leading-none">{fullName}</span>
        )}
        <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 font-mono text-[0.6875rem] text-muted-foreground">
          #{patientId}
        </span>
        {metaLabel && (
          <span className="shrink-0 text-sm font-semibold capitalize text-muted-foreground">
            {metaLabel}
          </span>
        )}
      </div>

      {/* Actions — Edit profile + Send a tip (Send a link removed). */}
      {patient && (
        <div className="flex shrink-0 items-center gap-1 pl-1">
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setEditOpen(true)}
            title={t('Edit patient profile')}
          >
            <Pencil className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2"
            onClick={() => setTipOpen(true)}
            title={t('Send a tip')}
          >
            <MessageSquarePlus className="h-3.5 w-3.5" />
          </Button>
          <PatientNotesButton onClick={() => setNotesOpen(true)} />
        </div>
      )}

      <EditPatientDialog
        open={editOpen}
        initial={patient ?? null}
        clinic={patientClinicsQuery.data?.[0]}
        clinicianFlags={clinicianFlags}
        onCancel={() => setEditOpen(false)}
        onSubmit={onSaveProfile}
        pending={updatePatient.isPending}
      />
      <SendTipDialog patientId={patientId} open={tipOpen} onOpenChange={setTipOpen} />
      <PatientNotesSheet patientId={patientId} open={notesOpen} onOpenChange={setNotesOpen} />
    </div>
  );
};
